/**
 *  @class
 *  @function LocalizationForm
 */
if (!customElements.get('custom-select')) {
  class CustomSelect extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('.thb-custom-select__toggle');
      this.list = this.querySelector('.thb-custom-select__list');
      this.valueEl = this.querySelector('.thb-custom-select__value');
      this.input = this.querySelector('input[type="hidden"]');
      this.options = Array.from(this.querySelectorAll('.thb-custom-select__option'));
      if (!this.toggle || !this.list || !this.input) return;

      this.onDocumentClick = this.onDocumentClick.bind(this);

      this.toggle.addEventListener('click', () => this.isOpen ? this.close() : this.open());
      this.toggle.addEventListener('keydown', this.onToggleKeydown.bind(this));
      this.list.addEventListener('keydown', this.onListKeydown.bind(this));
      this.options.forEach((option) => {
        option.addEventListener('click', () => this.select(option));
        option.addEventListener('mouseenter', () => this.setActive(option));
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    get isOpen() {
      return this.toggle.getAttribute('aria-expanded') === 'true';
    }

    open() {
      this.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.list.hidden = false;
      this.positionList();
      document.addEventListener('click', this.onDocumentClick);
      const current = this.options.find((o) => o.getAttribute('aria-selected') === 'true') || this.options[0];
      this.setActive(current);
      this.list.focus();
    }

    close(returnFocus) {
      this.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.list.hidden = true;
      document.removeEventListener('click', this.onDocumentClick);
      if (returnFocus) this.toggle.focus();
    }

    positionList() {
      const gutter = 12;
      const list = this.list;
      list.classList.remove('is-flipped');
      list.style.removeProperty('--select-offset');
      list.style.removeProperty('--select-max-height');

      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const toggleRect = this.toggle.getBoundingClientRect();
      const spaceBelow = viewportHeight - toggleRect.bottom - gutter;
      const spaceAbove = toggleRect.top - gutter;
      const flip = list.getBoundingClientRect().height > spaceBelow && spaceAbove > spaceBelow;
      list.classList.toggle('is-flipped', flip);
      list.style.setProperty('--select-max-height', `${Math.max(Math.floor((flip ? spaceAbove : spaceBelow) - 10), 120)}px`);

      const rect = list.getBoundingClientRect();
      let offset = 0;
      if (rect.right > viewportWidth - gutter) offset = viewportWidth - gutter - rect.right;
      if (rect.left + offset < gutter) offset = gutter - rect.left;
      if (offset) list.style.setProperty('--select-offset', `${Math.round(offset)}px`);
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.close();
    }

    setActive(option) {
      this.options.forEach((o) => o.classList.toggle('is-active', o === option));
      this.activeOption = option;
      if (option && typeof option.scrollIntoView === 'function') {
        option.scrollIntoView({ block: 'nearest' });
      }
    }

    moveActive(step) {
      const index = this.options.indexOf(this.activeOption);
      const next = Math.min(Math.max(index + step, 0), this.options.length - 1);
      this.setActive(this.options[next]);
    }

    onToggleKeydown(event) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        this.open();
      }
    }

    onListKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.moveActive(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.moveActive(-1);
          break;
        case 'Home':
          event.preventDefault();
          this.setActive(this.options[0]);
          break;
        case 'End':
          event.preventDefault();
          this.setActive(this.options[this.options.length - 1]);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (this.activeOption) this.select(this.activeOption);
          break;
        case 'Escape':
          event.preventDefault();
          this.close(true);
          break;
        case 'Tab':
          this.close();
          break;
      }
    }

    select(option) {
      const value = option.dataset.value;
      if (value === this.input.value) {
        this.close(true);
        return;
      }
      this.options.forEach((o) => o.setAttribute('aria-selected', String(o === option)));
      this.input.value = value;
      if (this.valueEl) this.valueEl.textContent = option.textContent;
      this.close();
      this.dispatchEvent(
        new CustomEvent('custom-select:change', {
          bubbles: true,
          detail: { input: this.input.name, value: value, country: option.dataset.country }
        })
      );
    }
  }
  customElements.define('custom-select', CustomSelect);
}

if (!customElements.get('localization-form')) {
  class LocalizationForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.addEventListener('custom-select:change', this.onSubmitHandler.bind(this));
    }
    onSubmitHandler(event) {
      if (!this.form) return;
      const detail = event.detail || {};
      if (detail.input === 'locale_code' && detail.country) {
        const countryInput = this.form.querySelector('input[name="country_code"]');
        if (countryInput) countryInput.value = detail.country;
      }
      this.form.submit();
    }
  }
  customElements.define('localization-form', LocalizationForm);
}
